import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import {
  HOME_CAROUSEL_MEDIA_EVENT,
  HOME_CAROUSEL_MEDIA_VERSION_KEY,
} from '/lib/homeCarouselInvalidation';
import {
  FaCompress,
  FaEllipsisV,
  FaExpand,
  FaPause,
  FaPlay,
  FaVolumeMute,
  FaVolumeUp,
} from 'react-icons/fa';
import videoContainerStyles from './VideoContainer.module.css';

function isYouTubeUrl(url) {
  return /youtube\.com|youtu\.be/.test(url || '');
}

function getYouTubeEmbedUrl(url) {
  try {
    const parsedUrl = new URL(url);
    let videoId = '';

    if (parsedUrl.hostname.includes('youtu.be')) {
      videoId = parsedUrl.pathname.replace('/', '');
    }

    if (parsedUrl.hostname.includes('youtube.com')) {
      videoId = parsedUrl.searchParams.get('v') || '';

      if (!videoId && parsedUrl.pathname.includes('/shorts/')) {
        videoId = parsedUrl.pathname.split('/shorts/')[1]?.split('/')[0] || '';
      }

      if (!videoId && parsedUrl.pathname.includes('/embed/')) {
        videoId = parsedUrl.pathname.split('/embed/')[1]?.split('/')[0] || '';
      }
    }

    if (!videoId) return null;

    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&playsinline=1&controls=1&rel=0`;
  } catch (error) {
    console.error('[VideoContainer] Invalid YouTube URL:', error);
    return null;
  }
}

function isDirectVideoUrl(url) {
  const path = String(url || '').split(/[?#]/, 1)[0];
  return /\.(mp4|webm|ogg|ogv|mov|m4v)$/i.test(path);
}

function formatTime(value) {
  const seconds = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

const VideoContainer = () => {
  const { data: session } = useSession();
  const [videos, setVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadedVideoUrls, setLoadedVideoUrls] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [frameTransition, setFrameTransition] = useState(null);
  const videoElements = useRef(new Map());
  const mediaAreaRef = useRef(null);
  const frameTransitionTimer = useRef(null);

  const activeVideo = useMemo(() => {
    if (!videos.length) return null;
    return videos[activeIndex] || videos[0];
  }, [videos, activeIndex]);

  useEffect(() => {
    let cancelled = false;
    let carouselSignature = '';

    const fetchVideos = async ({ initial = false } = {}) => {
      if (initial) {
        setLoading(true);
        setError('');
      }

      try {
        const res = await fetch('/api/homeCarousel/recentVideos', { cache: 'no-store' });
        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Failed to load carousel videos.');
        }

        if (!cancelled) {
          const nextVideos = Array.isArray(data.videos) ? data.videos : [];
          const nextSignature = nextVideos
            .map((video) => `${video.contentId}:${video.videoUrl}`)
            .join('|');

          if (nextSignature === carouselSignature) return;
          carouselSignature = nextSignature;
          setVideos(nextVideos);
          setLoadedVideoUrls((current) => current.filter((url) => (
            nextVideos.some((video) => video.videoUrl === url)
          )));
          setActiveIndex(0);
        }
      } catch (err) {
        console.error('[VideoContainer] fetchVideos error:', err);

        if (!cancelled && initial) {
          setError(err.message);
        }
      } finally {
        if (!cancelled && initial) {
          setLoading(false);
        }
      }
    };

    const revalidateVideos = () => fetchVideos();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidateVideos();
    };
    const handleStorage = (event) => {
      if (event.key === HOME_CAROUSEL_MEDIA_VERSION_KEY) revalidateVideos();
    };

    fetchVideos({ initial: true });
    window.addEventListener('focus', revalidateVideos);
    window.addEventListener(HOME_CAROUSEL_MEDIA_EVENT, revalidateVideos);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', revalidateVideos);
      window.removeEventListener(HOME_CAROUSEL_MEDIA_EVENT, revalidateVideos);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const videoUrl = activeVideo?.videoUrl;

    if (!isDirectVideoUrl(videoUrl)) return;

    setLoadedVideoUrls((current) =>
      current.includes(videoUrl) ? current : [...current, videoUrl]
    );
  }, [activeVideo]);

  useEffect(() => {
    videoElements.current.forEach((element, videoUrl) => {
      if (videoUrl === activeVideo?.videoUrl) {
        element.currentTime = 0;
        element.muted = isMuted;
        element.playbackRate = playbackRate;
        setCurrentTime(0);
        setDuration(Number.isFinite(element.duration) ? element.duration : 0);
        setOptionsOpen(false);
        const playback = element.play();
        if (playback?.catch) playback.catch(() => {});
      } else {
        element.pause();
      }
    });
  // Preserve the user's mute and speed choices while resetting each new slide.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVideo, loadedVideoUrls]);

  useEffect(() => {
    const activeElement = videoElements.current.get(activeVideo?.videoUrl);
    if (activeElement) activeElement.muted = isMuted;
  }, [activeVideo?.videoUrl, isMuted]);

  useEffect(() => {
    const activeElement = videoElements.current.get(activeVideo?.videoUrl);
    if (activeElement) activeElement.playbackRate = playbackRate;
  }, [activeVideo?.videoUrl, playbackRate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === mediaAreaRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const getActiveVideoElement = () => videoElements.current.get(activeVideo?.videoUrl);

  const togglePlayback = async () => {
    const element = getActiveVideoElement();
    if (!element) return;

    if (element.paused) {
      try {
        await element.play();
      } catch (playError) {
        console.error('[VideoContainer] Playback failed:', playError);
      }
    } else {
      element.pause();
    }
  };

  const toggleMute = () => setIsMuted((current) => !current);

  const seekVideo = (event) => {
    const element = getActiveVideoElement();
    if (!element) return;
    const nextTime = Number(event.target.value);
    element.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const restartVideo = () => {
    const element = getActiveVideoElement();
    if (!element) return;
    element.currentTime = 0;
    setCurrentTime(0);
    setOptionsOpen(false);
    const playback = element.play();
    if (playback?.catch) playback.catch(() => {});
  };

  const choosePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    setOptionsOpen(false);
  };

  const toggleFullscreen = async () => {
    const mediaArea = mediaAreaRef.current;
    if (!mediaArea) return;

    try {
      if (document.fullscreenElement === mediaArea) {
        await document.exitFullscreen();
      } else {
        await mediaArea.requestFullscreen();
      }
    } catch (fullscreenError) {
      console.error('[VideoContainer] Fullscreen failed:', fullscreenError);
    }
  };

  useEffect(() => () => {
    if (frameTransitionTimer.current) clearTimeout(frameTransitionTimer.current);
  }, []);

  const moveToVideo = (index, direction) => {
    if (!videos.length || index === activeIndex || frameTransition) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setActiveIndex(index);
      return;
    }

    setFrameTransition({ index, direction });
    frameTransitionTimer.current = setTimeout(() => {
      setActiveIndex(index);
      setFrameTransition(null);
      frameTransitionTimer.current = null;
    }, 700);
  };

  const goToPrevious = () => {
    if (!videos.length) return;
    moveToVideo((activeIndex - 1 + videos.length) % videos.length, 'previous');
  };

  const goToNext = () => {
    if (!videos.length) return;
    moveToVideo((activeIndex + 1) % videos.length, 'next');
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const target = event.target;
      const isEditing =
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

      if (isEditing || videos.length <= 1) return;

      event.preventDefault();
      const offset = event.key === 'ArrowLeft' ? -1 : 1;
      const index = (activeIndex + offset + videos.length) % videos.length;
      moveToVideo(index, offset < 0 ? 'previous' : 'next');
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // Rebind when navigation state changes so keyboard navigation uses the visible frame.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, frameTransition, videos.length]);

  const renderSidePreview = (offset, position) => {
    if (videos.length <= 1) return null;

    const index = (activeIndex + offset + videos.length) % videos.length;
    const video = videos[index];
    const directVideo = isDirectVideoUrl(video.videoUrl);

    return (
      <button
        key={`${position}-${index}`}
        type="button"
        className={`${videoContainerStyles.sideCard} ${
          position === 'previous'
            ? videoContainerStyles.sideCardPrevious
            : videoContainerStyles.sideCardNext
        } ${
          frameTransition?.index === index
            ? videoContainerStyles.sideCardTransitioning
            : ''
        }`}
        onClick={() => moveToVideo(index, position)}
        aria-label={`${position === 'previous' ? 'Previous' : 'Next'} video: ${
          video.name || 'ShuffleDB video'
        }`}
      >
        {directVideo ? (
          <video
            className={videoContainerStyles.sideCardVideo}
            src={video.videoUrl}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(event) => {
              const duration = Number(event.currentTarget.duration) || 0;
              event.currentTarget.currentTime = Math.min(0.1, duration / 2);
            }}
          />
        ) : (
          <div className={videoContainerStyles.sideCardFallback}>Video preview</div>
        )}
        <span className={videoContainerStyles.sideCardDirection} aria-hidden="true">
          {position === 'previous' ? '‹' : '›'}
        </span>
        <span className={videoContainerStyles.sideCardLabel}>
          <strong>{video.name || 'ShuffleDB video'}</strong>
          <small>
            {[video.type || 'Video', video.perspective, video.status]
              .filter(Boolean)
              .join(' · ')}
          </small>
        </span>
      </button>
    );
  };

  const renderVideo = () => {
    if (!activeVideo) return null;

    const { videoUrl, name } = activeVideo;

    if (isYouTubeUrl(videoUrl)) {
      const embedUrl = getYouTubeEmbedUrl(videoUrl);

      if (embedUrl) {
        return (
          <iframe
            key={videoUrl}
            className={videoContainerStyles.videoFrame}
            src={embedUrl}
            title={name || 'ShuffleDB video'}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        );
      }
    }

    if (isDirectVideoUrl(videoUrl)) return null;

    return (
      <div className={videoContainerStyles.unsupportedVideo}>
        <p>Video preview unavailable.</p>
        <a href={videoUrl} target="_blank" rel="noopener noreferrer">
          Open video
        </a>
      </div>
    );
  };

  return (
    <div
      className={`${videoContainerStyles.videoContainer} ${
        session?.user?.isAdmin === true ? videoContainerStyles.videoContainerAdmin : ''
      }`}
    >
      {loading ? (
        <div className={videoContainerStyles.placeholder}>Loading recent videos...</div>
      ) : error ? (
        <div className={videoContainerStyles.placeholder}>Unable to load videos.</div>
      ) : videos.length === 0 ? (
        <div className={videoContainerStyles.placeholder}>Recent videos will appear here.</div>
      ) : (
        <>
          {renderSidePreview(-1, 'previous')}
          <div ref={mediaAreaRef} className={videoContainerStyles.mediaArea}>
            {loadedVideoUrls.map((videoUrl) => {
              const video = videos.find((item) => item.videoUrl === videoUrl);
              const isActive = videoUrl === activeVideo?.videoUrl;

              return (
                <video
                  key={videoUrl}
                  ref={(element) => {
                    if (element) videoElements.current.set(videoUrl, element);
                    else videoElements.current.delete(videoUrl);
                  }}
                  className={`${videoContainerStyles.videoPlayer} ${
                    isActive ? '' : videoContainerStyles.videoPlayerHidden
                  }`}
                  src={videoUrl}
                  aria-label={video?.name || 'ShuffleDB video'}
                  autoPlay={isActive}
                  muted={isMuted}
                  playsInline
                  preload="auto"
                  onPlay={() => isActive && setIsPlaying(true)}
                  onPause={() => isActive && setIsPlaying(false)}
                  onEnded={() => isActive && setIsPlaying(false)}
                  onLoadedMetadata={(event) => {
                    if (!isActive) return;
                    setDuration(Number(event.currentTarget.duration) || 0);
                    setCurrentTime(Number(event.currentTarget.currentTime) || 0);
                  }}
                  onTimeUpdate={(event) => {
                    if (!isActive) return;
                    setCurrentTime(Number(event.currentTarget.currentTime) || 0);
                  }}
                />
              );
            })}
            {renderVideo()}
            {isDirectVideoUrl(activeVideo?.videoUrl) && (
              <div className={videoContainerStyles.customControls}>
                <button
                  type="button"
                  className={`${videoContainerStyles.controlButton} ${videoContainerStyles.muteButton}`}
                  onClick={toggleMute}
                  aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                </button>

                <div className={videoContainerStyles.upperRightControls}>
                  <button
                    type="button"
                    className={videoContainerStyles.controlButton}
                    onClick={toggleFullscreen}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  >
                    {isFullscreen ? <FaCompress /> : <FaExpand />}
                  </button>
                  <button
                    type="button"
                    className={videoContainerStyles.controlButton}
                    onClick={() => setOptionsOpen((current) => !current)}
                    aria-label="Video options"
                    aria-expanded={optionsOpen}
                    title="Video options"
                  >
                    <FaEllipsisV />
                  </button>
                  {optionsOpen && (
                    <div className={videoContainerStyles.optionsMenu}>
                      <button type="button" onClick={restartVideo}>Restart</button>
                      <span>Playback speed</span>
                      <div>
                        {[0.5, 1, 1.5].map((rate) => (
                          <button
                            key={rate}
                            type="button"
                            className={playbackRate === rate ? videoContainerStyles.optionActive : ''}
                            onClick={() => choosePlaybackRate(rate)}
                          >
                            {rate}×
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className={`${videoContainerStyles.centerPlayButton} ${
                    isPlaying ? videoContainerStyles.pauseButton : ''
                  }`}
                  onClick={togglePlayback}
                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>

                <div className={videoContainerStyles.bottomControls}>
                  <output aria-live="off">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </output>
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    step="0.01"
                    value={Math.min(currentTime, duration || 0)}
                    onChange={seekVideo}
                    aria-label="Video progress"
                    style={{
                      '--video-progress': `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={videoContainerStyles.mainDataPanel}>
            <strong className={videoContainerStyles.mainDataTitle}>
              {activeVideo.name || 'ShuffleDB video'}
            </strong>
            <div className={videoContainerStyles.mainDataMeta}>
              <span>{activeVideo.type || 'Video'}</span>
              {activeVideo.perspective && <span>{activeVideo.perspective} view</span>}
              {activeVideo.status && <span>{activeVideo.status}</span>}
              <span>Move #{activeVideo.contentId}</span>
              {activeVideo.publicHash && (
                <Link
                  href={`/ContentDisplay?hash=${encodeURIComponent(activeVideo.publicHash)}`}
                  className={videoContainerStyles.contentDisplayLink}
                  title={`View ${activeVideo.name || 'this item'} in ShuffleDB`}
                >
                  View Content ↗
                </Link>
              )}
              {session?.user?.isAdmin === true && activeVideo.contentId && (
                <Link
                  href={`/ContentAdmin?contentId=${encodeURIComponent(activeVideo.contentId)}`}
                  className={videoContainerStyles.contentAdminLink}
                  title={`Open ${activeVideo.name || 'this item'} in Content Admin`}
                >
                  Content Admin ↗
                </Link>
              )}
            </div>
          </div>
          {renderSidePreview(1, 'next')}

          <div className={videoContainerStyles.overlay}>
            <button
              type="button"
              className={videoContainerStyles.navButton}
              onClick={goToPrevious}
              aria-label="Previous video"
            >
              ‹
            </button>

            <button
              type="button"
              className={videoContainerStyles.navButton}
              onClick={goToNext}
              aria-label="Next video"
            >
              ›
            </button>
          </div>

          <div className={videoContainerStyles.dots}>
            {videos.map((video, index) => (
              <button
                key={`${video.contentId}-${video.videoUrl}-${index}`}
                type="button"
                className={`${videoContainerStyles.dot} ${
                  index === activeIndex ? videoContainerStyles.dotActive : ''
                }`}
                onClick={() => {
                  const previousIndex = (activeIndex - 1 + videos.length) % videos.length;
                  const nextIndex = (activeIndex + 1) % videos.length;

                  if (index === previousIndex) moveToVideo(index, 'previous');
                  else if (index === nextIndex) moveToVideo(index, 'next');
                  else if (!frameTransition) setActiveIndex(index);
                }}
                aria-label={`Show video ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default VideoContainer;
